'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, { useContext, useEffect, useState } from 'react'
import { Formik } from 'formik'
import moment from 'moment'

// walrus
import Form from '@components/kit/form'
import { accessibilityLabel } from '@components/kit/_support'
import Box from '@components/kit/box'
import Card from '@components/kit/card'
import Text from '@components/kit/text'
import { Column, Row } from '@components/kit/grid'
import Loading from '@components/kit/loading'

// lib
import { ChatContext } from '../chat.web'
import Button from '@components/kit/button'
import Modal from '@components/kit/modal'
import { FormButtonPanel, TextFieldFormated } from '@components/formFields'
import { IUserEngagements } from '@store/Incentive/types'
import { IUserAddress } from '@store/Address/types'
import { useIncentiveStore } from '@store/Incentive'
import { useAddressStore } from '@store/Address'

/* -----------------------------------------------------------------------------
 * RequestNewBenefitsCardModal
 * -------------------------------------------------------------------------- */

type RequestNewBenefitsCardModalProps = {
  open: boolean
  onClose: () => void
}

const RequestNewBenefitsCardModal = ({
  open,
  onClose
}: RequestNewBenefitsCardModalProps) => {
  const { userId } = useContext(ChatContext)
  const { getUserAddress, setUserAddress } = useAddressStore.getState()
  const { requestNewCard } = useIncentiveStore.getState()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isConclusionModalOpen, setIsConclusionModalOpen] = useState(false)
  const [isFormError, setIsFormError] = useState(false)
  const [isResponseError, setIsResponseError] = useState(false)
  const [responseErrorMessage, setResponseErrorMessage] = useState('')
  const [address, setAddress] = useState<IUserAddress>({
    name: '',
    line: '',
    city: '',
    state: '',
    zipcode: ''
  })

  const handleAddressChange = (field: keyof IUserAddress, value: string) => {
    setAddress(prevAddress => ({
      ...prevAddress,
      [field]: value
    }))
  }

  const handleSubmit = async () => {
    if (isSubmitting) {
      return
    }
    if (
      !address.name.trim() ||
      !address.line.trim() ||
      !address.city.trim() ||
      !address.state.trim() ||
      !address.zipcode.trim()
    ) {
      setIsFormError(true)
      return
    }
    setIsFormError(false)
    setIsSubmitting(true)
    let success = false
    let errorMessage
    ;({ success, errorMessage } = await setUserAddress(userId, address))
    if (success) {
      ;({ success, errorMessage } = await requestNewCard(userId))
    }
    setIsSubmitting(false)
    onClose()
    setIsResponseError(!success)
    setResponseErrorMessage(errorMessage || '')
    setIsConclusionModalOpen(true)
  }

  const handleCloseConclusionModal = () => {
    setIsConclusionModalOpen(false)
    setIsResponseError(false)
  }

  useEffect(() => {
    const fetchAddress = async () => {
      const userAddress = await getUserAddress(userId)
      if (userAddress) {
        setAddress(userAddress)
      }
    }
    if (open) {
      fetchAddress()
    }
  }, [getUserAddress, open, userId])

  return (
    <>
      <Modal
        open={open}
        onRequestClose={onClose}
        showCloseButton={false}
        maxWidth={411}
        borderRadius='medium'
      >
        <Box p='00' {...accessibilityLabel('RequestNewBenefitsCardModal')}>
          <Text variant='h3' mb='16'>
            Request New Benefits Card Modal
          </Text>
          <Text mb='20' variant='body'>
            Confirm or change member mailing address for new benefits card
          </Text>

          <Formik
            initialValues={{}}
            onSubmit={() => {}}
            validateOnChange={true}
          >
            <Form>
              <Box gap='04'>
                <TextFieldFormated
                  label='Name'
                  name='name'
                  value={address.name}
                  editable={!isSubmitting}
                  onChangeText={value => handleAddressChange('name', value)}
                  required
                />
                <TextFieldFormated
                  label='Address'
                  name='line'
                  value={address.line}
                  editable={!isSubmitting}
                  onChangeText={value => handleAddressChange('line', value)}
                  required
                />
                <TextFieldFormated
                  label='City'
                  name='city'
                  value={address.city}
                  editable={!isSubmitting}
                  onChangeText={value => handleAddressChange('city', value)}
                  required
                />
                <TextFieldFormated
                  label='State'
                  name='state'
                  value={address.state}
                  editable={!isSubmitting}
                  onChangeText={value => handleAddressChange('state', value)}
                  required
                />
                <TextFieldFormated
                  label='Zip Code'
                  name='zipcode'
                  value={address.zipcode}
                  editable={!isSubmitting}
                  onChangeText={value => handleAddressChange('zipcode', value)}
                  required
                />
              </Box>
              {isFormError && (
                <Text mt='08' textColor='red' font='text.12.medium'>
                  * Please fill in all address fields.
                </Text>
              )}
              <FormButtonPanel
                onCancel={onClose}
                onSave={handleSubmit}
                saveLabel='Submit Request'
              />
            </Form>
          </Formik>
        </Box>
        {isSubmitting && (
          <Box
            position='absolute'
            width='100%'
            height='100%'
            top='0'
            left='0'
            right='0'
            flex='1'
            justifyContent='center'
            alignItems='center'
            style={{
              backgroundColor: '#f4f7facc'
            }}
          >
            <Loading size='large' />
          </Box>
        )}
      </Modal>
      <Modal
        open={isConclusionModalOpen}
        onRequestClose={handleCloseConclusionModal}
        showCloseButton={false}
        maxWidth={411}
        borderRadius='medium'
        backgroundColor={isResponseError ? 'walrusRed050' : 'mintGreen'}
      >
        <Box p='00'>
          <Text
            variant='h3'
            mb='16'
            textColor={isResponseError ? 'paleRed' : 'paleGreen'}
          >
            {isResponseError ? 'Error' : 'Request Submitted'}
          </Text>
          <Text mb='20' variant='body'>
            {isResponseError
              ? 'There was an error processing your request.'
              : 'Your request for a new benefits card has been submitted successfully.'}
          </Text>
          {isResponseError && responseErrorMessage && (
            <Box
              backgroundColor='paleError'
              p='12'
              borderRadius='medium'
              mb='20'
            >
              <Text variant='body' textColor='paleRed'>
                {responseErrorMessage.charAt(0).toUpperCase() +
                  responseErrorMessage.slice(1)}
              </Text>
            </Box>
          )}
          <Button variant='secondary' onPress={handleCloseConclusionModal}>
            Close
          </Button>
        </Box>
      </Modal>
    </>
  )
}

/* -----------------------------------------------------------------------------
 * helpers
 * -------------------------------------------------------------------------- */

const shouldShowEngagement = (engagement: string) => {
  return (
    engagement !== 'CHAT_SUGGESTION_VIEW' && engagement !== 'CALL_DISCUSSION'
  )
}

const getEngagementLabel = (engagement: string) => {
  switch (engagement) {
    case 'SYMPTOM_TRACKING':
      return 'Tracked symptoms'
    case 'CHAT_MESSAGE':
      return 'Message Sent'
    case 'POLL_ANSWER':
      return 'Poll answered'
    case 'POLL_V2_ANSWER':
      return 'Poll v2 answered'
    default:
      return engagement
  }
}

/* -----------------------------------------------------------------------------
 * MemberPlan
 * -------------------------------------------------------------------------- */

const MemberPlan = () => {
  const { userId, userInfo } = useContext(ChatContext)
  const { getUserEngagements } = useIncentiveStore.getState()
  const [data, setData] = useState<IUserEngagements | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setIsLoading(true)
    getUserEngagements(userId).then(engagements => {
      setData(engagements)
      setIsLoading(false)
    })
  }, [getUserEngagements, userId])
  const [isRequestNewCardModalOpen, setIsRequestNewCardModalOpen] =
    useState(false)

  const isLoadingUserInfo = !userInfo

  const isDependentInfoPresent =
    userInfo?.dependent !== undefined && userInfo?.dependent !== null
  const coverageType = userInfo?.dependent ? 'Dependent' : 'Primary'

  return (
    <Box pt='24' {...accessibilityLabel('MemberPlan')}>
      <Text ml='12' mb='08' font='display.16.bold'>
        Plan
      </Text>
      {userInfo?.incentive.is_enrolled && (
        <Box mb='08' flexDirection='row' gap='04'>
          <Button
            onPress={() => setIsRequestNewCardModalOpen(true)}
            size='fit'
            variant='tertiary'
          >
            Request New Benefits Card
          </Button>
        </Box>
      )}
      <Card
        size='medium'
        flexDirection='column'
        justifyContent='space-between'
        alignItems='center'
        minHeight='72px'
      >
        <Box width='100%'>
          <Row marginBottom='02'>
            <Column width='70%'>
              <Text textColor='newMutedPurple' font='display.12.bold'>
                Plan Name
              </Text>
            </Column>
            <Column width='30%'>
              <Text textColor='newMutedPurple' font='display.12.bold'>
                Coverage Type
              </Text>
            </Column>
          </Row>

          <Row>
            <Column width='70%'>
              {userInfo?.plan ? (
                <Text font='display.12.bold'>{userInfo.plan.name}</Text>
              ) : null}
            </Column>
            <Column width='30%'>
              {!isLoadingUserInfo &&
                isDependentInfoPresent &&
                userInfo.incentive.is_enrolled && (
                  <Text font='display.12.bold'>{coverageType}</Text>
                )}
            </Column>
          </Row>
        </Box>

        {isLoading ? (
          <Box mt='24'>
            <Loading py='20' />
          </Box>
        ) : (
          <Box width='100%' mt='24'>
            <Text mb='08' font='display.16.bold'>
              Benefits
            </Text>
            <Box width='100%' height='1px' backgroundColor='shade02' mb='16' />

            <Row mb='08'>
              <Column width='50%'>
                <Text font='display.12.bold'>Incentive Plan</Text>
              </Column>
              <Column width='25%'>
                <Text textColor='newMutedPurple' font='display.12.bold'>
                  Start date
                </Text>
              </Column>
              <Column width='25%'>
                <Text textColor='newMutedPurple' font='display.12.bold'>
                  End date
                </Text>
              </Column>
            </Row>

            <Row>
              <Column width='50%'></Column>
              <Column width='25%'>
                <Text textColor='newMutedPurple' font='text.12.medium'>
                  {data?.plan.start_date ?? 'Not enrolled'}
                </Text>
              </Column>
              <Column width='25%'>
                <Text textColor='newMutedPurple' font='text.12.medium'>
                  {data?.plan.end_date ?? ''}
                </Text>
              </Column>
            </Row>

            {data?.engagements && data.engagements.length > 0 ? (
              <Box width='100%' mt='24'>
                <Row>
                  <Column width='30%'>
                    <Text font='display.14.bold'>Activity</Text>
                  </Column>
                  <Column width='70%' />
                </Row>

                {data?.engagements.map(engagement => (
                  <Row key={engagement.month_year} mt='08'>
                    <Column width='30%'>
                      <Text font='text.12.bold' textColor='newMutedPurple'>
                        {moment(engagement.month_year, 'YYYY/MM').format(
                          'MMMM YYYY'
                        )}
                      </Text>
                    </Column>
                    <Column width='70%'>
                      <Text font='text.12.medium' textColor='newMutedPurple'>
                        {engagement.name.length > 0
                          ? engagement.name
                              .filter(shouldShowEngagement)
                              .map(name =>
                                getEngagementLabel(name)
                                  .toLowerCase()
                                  .replace(/_/g, ' ')
                                  .split(' ')
                                  .map(
                                    word =>
                                      word.charAt(0).toUpperCase() +
                                      word.slice(1)
                                  )
                                  .join(' ')
                              )
                              .join(', ')
                          : 'None'}
                      </Text>
                    </Column>
                  </Row>
                ))}
              </Box>
            ) : null}
          </Box>
        )}
      </Card>
      <RequestNewBenefitsCardModal
        open={isRequestNewCardModalOpen}
        onClose={() => setIsRequestNewCardModalOpen(false)}
      />
    </Box>
  )
}

export default MemberPlan
